import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Analytics } from '@vercel/analytics/react';

import ArticlePage from './pages/ArticlePage';

import InputsPanel from './components/InputsPanel';
import TabStrip from './components/Tabs';
import ModeToggle from './components/ModeToggle';

// Existing panels (each renders its own sketch-card)
import SnapshotCard from './components/SnapshotCard';
import DonutChart from './components/DonutChart';
import SensitivityTable from './components/SensitivityTable';
import MyProductsPanel from './components/MyProductsPanel';
import ProfitPriceFinderBar from './components/ProfitPriceFinderBar';
import BreakEvenBar from './components/BreakEvenBar';

// New tab panels
import UpdatesPanel from './components/UpdatesPanel';
import ForecastPanel from './components/ForecastPanel';
import PlatformsPanel from './components/PlatformsPanel';
import ReportsPanel from './components/ReportsPanel';
import TopNav from './components/TopNav';
import FAQSection from './components/FAQSection';
import ArticlesSection from './components/ArticlesSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';

import CheatSheet from './components/CheatSheet';
import CurrencyConverter from './components/CurrencyConverter';

import { calculate, findPriceForMargin } from './lib/calculations';
import { encodeShareUrl, decodeShareUrl, hasShareParams } from './lib/share';
import { generateInsights } from './lib/insights';
import { OFFSITE_RATES } from './data/countries';
import {
  fetchLiveRates,
  getLastFetched,
  getRatesSource,
  subscribeRates,
} from './data/exchangeRates';

import './index.css';

// ===== Defaults =====
const DEFAULT_INPUTS = {
  price: '', shipping: '', shippingCost: '', cost: '',
  quantity: 1, discount: '', discountType: 'dollar', desiredProfit: '',
  laborHours: '', laborRate: '', packaging: '', returnRate: '',
};
const DEFAULT_SETTINGS = {
  country: 'US',
  offsiteEnabled: false,
  offsiteRate: OFFSITE_RATES.under10k,
  orderType: 'separate',  // 'separate' (q individual buyers) | 'bulk' (one buyer, one transaction)
  displayCurrency: 'USD', // ISO code for what currency the UI shows numbers in. Updates with country pick by default.,
  taxRate: 0,
  productCategory: 'none',
};

// What used to be TOP_TABS (Volume / Forecast / Insights) is gone:
//   - Insights moves to the top row alongside Snapshot + Donut (always visible)
//   - Volume + Forecast combine into a single "Sales projections" section
// What remains as a bottom tab strip is just the supplementary reference content.
const BOTTOM_TABS = [
  { id: 'platforms', icon: '🏢', label: 'Platforms' },
  { id: 'updates',   icon: '📅', label: 'Updates' },
  { id: 'cheat',     icon: '📖', label: 'Cheat Sheet' },
];

// First-visit seed products — pre-populated so MyProducts/Compare demo themselves.
// These represent realistic Etsy items at different price points so a new
// user can immediately try Compare and see how the feature works.
const SAMPLE_PRODUCTS = [
  {
    id: 1000000001,
    name: 'Hand-stitched Earrings',
    savedAt: '2026-04-01T12:00:00.000Z',
    price: '24',
    quantity: 1,
    profit: 17.79,
    margin: 67.4,
    inputs: {
      price: '24', cost: '3', quantity: 1, shipping: '4', shippingCost: '3',
      packaging: '1', laborHours: '0.5', laborRate: '15',
      discount: '', discountType: 'dollar', returnRate: '',
      isExample: true,
    },
    settings: {
      country: 'US', offsiteEnabled: false, offsiteRate: 0.15,
      orderType: 'separate', displayCurrency: 'USD',
    },
  },
  {
    id: 1000000002,
    name: 'Leather Card Wallet',
    savedAt: '2026-04-10T12:00:00.000Z',
    price: '48',
    quantity: 1,
    profit: 27.85,
    margin: 53.6,
    inputs: {
      price: '48', cost: '12', quantity: 1, shipping: '4', shippingCost: '4',
      packaging: '2', laborHours: '1', laborRate: '20',
      discount: '', discountType: 'dollar', returnRate: '',
      isExample: true,
    },
    settings: {
      country: 'US', offsiteEnabled: true, offsiteRate: 0.15,
      orderType: 'separate', displayCurrency: 'USD',
    },
  },
  {
    id: 1000000003,
    name: 'Soy Wax Candle Set',
    savedAt: '2026-04-15T12:00:00.000Z',
    price: '32',
    quantity: 1,
    profit: 14.21,
    margin: 39.5,
    inputs: {
      price: '32', cost: '8', quantity: 1, shipping: '6', shippingCost: '6',
      packaging: '2', laborHours: '0.4', laborRate: '18',
      discount: '', discountType: 'dollar', returnRate: '',
      isExample: true,
    },
    settings: {
      country: 'US', offsiteEnabled: false, offsiteRate: 0.15,
      orderType: 'separate', displayCurrency: 'USD',
    },
  },
];



// ===== localStorage helpers =====
const loadObj = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch { return fallback; }
};
const loadArr = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

function HomePage() {
  // Scroll to anchor when arriving with a /#section URL (e.g. clicking
  // "FAQ" in the nav from an article page navigates to /#faq, and this
  // effect scrolls the homepage to that section once it's mounted).
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // Small delay so layout is in place before scrolling
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [location]);

  // ─── State ──────────────────────────────────────────────
  const [inputs,    setInputs]    = useState(() => loadObj('etsyCalculator',          DEFAULT_INPUTS));
  const [settings,  setSettings]  = useState(() => loadObj('etsyCalculatorSettings',  DEFAULT_SETTINGS));
  const [scenarios, setScenarios] = useState(() => loadArr('etsyCalculatorScenarios'));
  const [result,    setResult]    = useState(null);
  const [errors,    setErrors]    = useState({});
  const [showHelp,  setShowHelp]  = useState(false);
  const [copyStatus, setCopyStatus] = useState('');

  const [bottomTab, setBottomTab] = useState('platforms');
  // Top-level workspace mode: 'calculate' | 'reports'
  // Calculate = the unified single-screen calculator (inputs + snapshot + donut +
  // insights + profit finder + my products + projections + supplementary tabs).
  // Reports = full-width historical sales analysis with no inputs panel.
  const [mode, setMode] = useState('calculate');

  // Exchange rates: track a "version" that bumps when rates update, so all
  // calculations re-run with the new rates. Components also read getLastFetched()
  // / getRatesSource() to display freshness info.
  const [ratesVersion, setRatesVersion] = useState(0);
  const [ratesStatus, setRatesStatus] = useState({
    fetching: false,
    lastFetched: getLastFetched(),
    source: getRatesSource(),
    error: null,
  });

  const refreshRates = async (force = true) => {
    setRatesStatus(s => ({ ...s, fetching: true, error: null }));
    const result = await fetchLiveRates({ force });
    setRatesStatus({
      fetching: false,
      lastFetched: getLastFetched(),
      source: getRatesSource(),
      error: result.ok ? null : result.error,
    });
    setRatesVersion(v => v + 1);
  };

  // Subscribe to rate-updates from any source (e.g. another tab)
  useEffect(() => {
    const unsub = subscribeRates(() => {
      setRatesStatus(s => ({ ...s, lastFetched: getLastFetched(), source: getRatesSource() }));
      setRatesVersion(v => v + 1);
    });
    return unsub;
  }, []);

  // Fetch fresh rates on first mount (respects cache TTL)
  useEffect(() => { refreshRates(false); /* eslint-disable-next-line */ }, []);

  // ─── Persist ────────────────────────────────────────────
  useEffect(() => { localStorage.setItem('etsyCalculator',         JSON.stringify(inputs)); }, [inputs]);
  useEffect(() => { localStorage.setItem('etsyCalculatorSettings', JSON.stringify(settings)); }, [settings]);
  useEffect(() => { localStorage.setItem('etsyCalculatorScenarios', JSON.stringify(scenarios)); }, [scenarios]);

  // ─── Shared link on mount ───────────────────────────────
  // If the URL has share params, override the loaded state with the shared
  // values, run the calc immediately so the recipient sees results, then
  // clean the URL so subsequent edits don't carry stale params around.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasShareParams(window.location.search)) return;

    const { inputs: shareIn, settings: shareSet } = decodeShareUrl(window.location.search);
    const mergedInputs = { ...DEFAULT_INPUTS, ...shareIn };
    const mergedSettings = { ...DEFAULT_SETTINGS, ...shareSet };
    setInputs(mergedInputs);
    setSettings(mergedSettings);
    setResult(calculate(mergedInputs, mergedSettings));

    // Clean the URL so user doesn't see the params after loading
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Re-calc on input/settings/rates change *only if already calculated once*
  useEffect(() => {
    if (result) setResult(calculate(inputs, settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, settings, ratesVersion]);

  // ─── Derived ────────────────────────────────────────────
  // (Volume Sandbox now computes its own per-volume rows internally,
  // so we no longer pre-derive a sensitivity table here.)
  const insights = useMemo(
    () => generateInsights(result, inputs, settings),
    [result, inputs, settings]
  );

  // ─── Handlers ───────────────────────────────────────────
  const handleCalculate = () => {
    const e = {};
    ['price', 'cost', 'quantity'].forEach(f => {
      if (!inputs[f] || isNaN(inputs[f]) || Number(inputs[f]) <= 0) {
        e[f] = `Need a valid ${f}`;
      }
    });
    setErrors(e);
    if (Object.keys(e).length === 0) setResult(calculate(inputs, settings));
  };

  const handleReset = () => {
    if (!window.confirm('Clear all inputs and saved products?')) return;
    setInputs(DEFAULT_INPUTS);
    setSettings(DEFAULT_SETTINGS);
    setScenarios([]);
    setResult(null);
    setErrors({});
    setActionFlash(null);
    setResetCounter(c => c + 1);  // forces remount of components with internal state
  };


  const [shareStatus, setShareStatus] = useState('');
  const [actionFlash, setActionFlash] = useState(null);
  const [resetCounter, setResetCounter] = useState(0);
  const handleShareLink = async () => {
    const url = encodeShareUrl(inputs, settings);
    try {
      await navigator.clipboard.writeText(url);
      setShareStatus('link copied!');
    } catch {
      setShareStatus('copy failed');
    }
    setTimeout(() => setShareStatus(''), 1500);
  };

  const handleCopy = async () => {
    if (!result) return;
    const sym = result.currencySymbol;
    const lines = [
      'Etsy Sale Summary',
      '─────────────────',
      `Price: ${sym}${inputs.price} × ${result.quantity}`,
      `Revenue: ${sym}${result.revenue.toFixed(2)}`,
      '',
      `Listing fee:     ${sym}${result.listingFee.toFixed(2)}`,
      `Transaction fee: ${sym}${result.transactionFee.toFixed(2)}`,
      `Processing fee:  ${sym}${result.processingFee.toFixed(2)}`,
      ...(result.regulatoryFee > 0 ? [`Regulatory fee:  ${sym}${result.regulatoryFee.toFixed(2)}`] : []),
      ...(result.offsiteFee > 0    ? [`Offsite Ads:     ${sym}${result.offsiteFee.toFixed(2)}`]    : []),
      `Total Etsy fees: ${sym}${result.totalEtsyFees.toFixed(2)} (${result.effectiveFeeRate}%)`,
      '',
      `Total cost:      ${sym}${result.totalCost.toFixed(2)}`,
      `NET PROFIT:      ${sym}${result.profit.toFixed(2)} (${result.margin}% margin)`,
      '',
      `→ ${encodeShareUrl(inputs, settings)}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopyStatus('copied!');
    } catch {
      setCopyStatus('copy failed');
    }
    setTimeout(() => setCopyStatus(''), 1500);
  };

  const saveScenario = (name) => {
    if (!result) return;
    const snap = {
      id: Date.now(), name, savedAt: new Date().toISOString(),
      inputs: { ...inputs }, settings: { ...settings },
      price: inputs.price, quantity: result.quantity,
      profit: result.profit, margin: result.margin,
    };
    setScenarios(prev => [snap, ...prev].slice(0, 30));
  };
  const loadScenario = (s) => {
    setInputs(s.inputs);
    setSettings(s.settings);
    setTimeout(() => setResult(calculate(s.inputs, s.settings)), 0);
  };
  const deleteScenario = (id) => setScenarios(p => p.filter(s => s.id !== id));

  // ─── Manual: load the example products into the saved list ─────────
  // Triggered from the empty-state button in MyProductsPanel.
  // Replaces the deleted auto-seed flow so users opt in to seeing samples.
  const loadExampleProducts = () => {
    setScenarios(prev => {
      // If user already has products, append (don't replace) — defensive
      if (prev && prev.length > 0) return prev;
      return SAMPLE_PRODUCTS;
    });
  };


  // ─── Apply suggested action from an insight ───────────────────────
  // Each insight may include an `action` like { type, value }; this maps it
  // to a state mutation + recalculation, then shows a flash message confirming
  // what changed (so the disappearing insight button doesn't feel like a no-op).
  const handleApplyAction = (action) => {
    if (!action || !action.type) return;
    const prevInputs = { ...inputs };
    const prevSettings = { ...settings };
    const prevResult = result;
    const sym = prevResult?.currencySymbol || '$';
    let nextInputs = { ...inputs };
    let nextSettings = { ...settings };

    switch (action.type) {
      case 'setOffsite':
        nextSettings.offsiteEnabled = !!action.value;
        break;
      case 'setTax':
        nextSettings.taxRate = action.value;
        break;
      case 'setOrderType':
        nextSettings.orderType = action.value;
        break;
      case 'rollShipping': {
        const charged = parseFloat(inputs.shipping) || 0;
        const price = parseFloat(inputs.price) || 0;
        nextInputs.price = String((price + charged).toFixed(2));
        nextInputs.shipping = '0';
        break;
      }
      case 'setPriceMin': {
        const minPrice = action.value;
        const cur = parseFloat(inputs.price) || 0;
        if (cur < minPrice) nextInputs.price = String(minPrice);
        break;
      }
      case 'setMargin': {
        const newPrice = findPriceForMargin(inputs, settings, action.value);
        if (newPrice && newPrice.exact && newPrice.exact > 0) {
          nextInputs.price = String(newPrice.exact.toFixed(2));
        }
        break;
      }
      case 'setHourly': {
        const laborHours = parseFloat(inputs.laborHours) || 0;
        const q = parseInt(inputs.quantity, 10) || 1;
        if (laborHours > 0 && q > 0 && result) {
          const targetTotalProfit = action.value * laborHours * q;
          const profitDelta = targetTotalProfit - result.profit;
          const cur = parseFloat(inputs.price) || 0;
          nextInputs.price = String((cur + profitDelta / q).toFixed(2));
        }
        break;
      }
      default:
        return;
    }

    // Compute new result synchronously so the flash message can include the new number
    const newResult = calculate(nextInputs, nextSettings);
    setInputs(nextInputs);
    setSettings(nextSettings);
    setResult(newResult);

    // Build a clear confirmation message so the user knows what changed
    let msg = '';
    switch (action.type) {
      case 'setTax':
        msg = action.value > 0
          ? `✓ Set aside ${action.value}% for taxes — you keep ${sym}${newResult.afterTaxProfit.toFixed(2)} of ${sym}${newResult.profit.toFixed(2)}`
          : `✓ Tax set-aside off`;
        break;
      case 'setOffsite':
        msg = action.value
          ? `✓ Offsite Ads on — Net Profit ${sym}${newResult.profit.toFixed(2)} (was ${sym}${prevResult.profit.toFixed(2)})`
          : `✓ Offsite Ads off — Net Profit ${sym}${newResult.profit.toFixed(2)}`;
        break;
      case 'setMargin':
        msg = `✓ Re-priced for ${action.value}% margin — new price ${sym}${nextInputs.price}`;
        break;
      case 'rollShipping':
        msg = `✓ Shipping rolled into item price (now ${sym}${nextInputs.price})`;
        break;
      case 'setPriceMin':
        msg = `✓ Price raised to ${sym}${action.value} — Net Profit ${sym}${newResult.profit.toFixed(2)}`;
        break;
      case 'setOrderType':
        msg = `✓ Switched to ${action.value} order math — Net Profit ${sym}${newResult.profit.toFixed(2)}`;
        break;
      case 'setHourly':
        msg = `✓ Re-priced for ${sym}${action.value}/hour target — new price ${sym}${nextInputs.price}`;
        break;
      default:
        msg = '✓ Applied';
    }

    setActionFlash({
      message: msg,
      undo: () => {
        setInputs(prevInputs);
        setSettings(prevSettings);
        setResult(prevResult);
        setActionFlash(null);
      },
    });
    if (window.__flashTimeout) clearTimeout(window.__flashTimeout);
    window.__flashTimeout = setTimeout(() => setActionFlash(null), 6000);
  };

  // Single-modifier clear (used by SnapshotCard's modifier chips)
  const handleClearModifier = (name) => {
    setSettings(prev => {
      const next = { ...prev };
      if (name === 'tax') next.taxRate = 0;
      else if (name === 'offsite') next.offsiteEnabled = false;
      else if (name === 'bulk') next.orderType = 'separate';
      else return prev;
      // Recalculate immediately
      setTimeout(() => setResult(calculate(inputs, next)), 0);
      return next;
    });
  };

  // ─── Bottom-tab content ─────────────────────────────────
  // What's here is supplementary reference content that lives inside Calculate
  // mode. Volume/Forecast/Insights/MyProducts/ProfitFinder/Reports have all
  // been promoted to the main flow (or to the Reports mode); these three are
  // the leftovers that don't change with inputs.
  const renderBottomTab = () => {
    switch (bottomTab) {
      case 'platforms': return <PlatformsPanel inputs={inputs} settings={settings} currencySymbol={result?.currencySymbol || '$'} />;
      case 'updates': return <UpdatesPanel inputs={inputs} settings={settings} result={result} />;
      case 'cheat': return (
        <div className="sketch-card">
          <h2 className="font-display text-2xl text-ink mb-1">📖 Cheat Sheet</h2>
          <CheatSheet defaultCountry={settings.country} />
        </div>
      );
      default: return null;
    }
  };

  // ─── Render ─────────────────────────────────────────────
  return (
    <>
      <TopNav />
      <div id="home" className="min-h-screen text-ink px-4 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="text-center mb-6 relative pt-2">
          <h1 className="font-title text-title text-ink leading-none">Etsy Fee Calculator</h1>
          <p className="font-note text-xl text-ink/82 mt-1">
            ~ pricing your craft, one sketch at a time ~
          </p>
          {/* (Credibility messaging now lives in the nav as a vertical
              scrolling ticker — see TopNav.js — so the hero stays clean.) */}
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="absolute top-0 right-0 p-2 text-ink/75 hover:text-primary transition-colors"
            aria-label="Help"
          >
            <InformationCircleIcon className="w-7 h-7" />
          </button>
          {showHelp && (
            <div className="sticky-note bg-yellow-100 mt-4 max-w-2xl mx-auto text-left -rotate-1">
              <p className="font-sketch text-body">
                Use the <strong>Calculate / Reports</strong> toggle above the inputs panel
                to switch modes. In <strong>Calculate</strong>, fill in your numbers, tap{' '}
                <span className="font-title text-2xl text-primary">Calculate</span>, and
                everything flows down: Snapshot, Donut, Insights, target-price finder, your
                saved products, and projections — all on one page. In <strong>Reports</strong>,
                upload an Etsy CSV to analyze your historical sales.
              </p>
            </div>
          )}
        </header>

        {mode === 'reports' ? (
          /* Reports mode: full-width with a sticky toggle in the top-left.
             Constrain the toggle to a sensible width — the underlying control
             is now full-width-by-default, so without this wrapper it would
             stretch across the whole page. */
          <>
            <div style={{ marginBottom: '1.25rem', maxWidth: '380px' }}>
              <ModeToggle mode={mode} onChange={setMode} />
            </div>
            <ReportsPanel settings={settings} />
          </>
        ) : (
          /* Calculate mode: 2-column grid — inputs (left) | main flow (right).
             Insights are folded into the DonutChart as inline badges, so there
             is no separate Insights column anymore. */
          <div className="grid gap-5 lg:grid-cols-[minmax(320px,28%)_minmax(0,1fr)] items-start">
            {/* Left column: toggle + inputs (sticky). The toggle is now
                full-width and styled to match the inputs sketch-card below,
                so they read as a stacked pair rather than a floating
                widget over a card. */}
            <aside className="lg:sticky lg:top-4 lg:self-start" style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <ModeToggle mode={mode} onChange={setMode} />
              <InputsPanel
                inputs={inputs} setInputs={setInputs}
                settings={settings} setSettings={setSettings}
                errors={errors}
                onCalculate={handleCalculate}
                onReset={handleReset}
                ratesStatus={ratesStatus}
                onRefreshRates={() => refreshRates(true)}
              />
            </aside>

            <section className="space-y-5 min-w-0">
              {/* Snapshot + Donut (with inline insights) — original pairing */}
              {result ? (
                <div className="grid gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] items-stretch">
                  <SnapshotCard
                    result={result}
                    onCopy={handleCopy}
                    onShareLink={handleShareLink}
                    shareStatus={shareStatus}
                    copyStatus={copyStatus}
                    settings={settings}
                    onClearModifier={handleClearModifier}
                  />
                  <DonutChart
                    result={result}
                    insights={insights}
                    onApplyAction={handleApplyAction}
                    flashMessage={actionFlash}
                    onDismissFlash={() => setActionFlash(null)}
                  />
                </div>
              ) : (
                <div className="sketch-card flex flex-col items-center justify-center text-center" style={{ minHeight: '300px' }}>
                  <div className="text-6xl mb-4" aria-hidden="true">📐</div>
                  <h2 className="font-display text-2xl text-ink mb-2">Ready when you are</h2>
                  <p className="font-body text-base text-ink/78 max-w-md">
                    Fill in your numbers on the left and tap{' '}
                    <span className="font-semibold text-primary">Calculate</span>.
                    Your <span className="font-semibold">Snapshot</span> and{' '}
                    <span className="font-semibold">Donut breakdown</span> (with inline insights) will appear here.
                  </p>
                </div>
              )}

              {/* Profit-Price Finder + Break-Even — standalone analyst bars.
                  Both use the same dark-header pattern so they read as a pair;
                  Break-Even is purple-accented to differentiate. */}
              {result && (
                <ProfitPriceFinderBar
                  key={`ppf-${resetCounter}`}
                  inputs={inputs}
                  setInputs={setInputs}
                  settings={settings}
                  currencySymbol={result.currencySymbol || '$'}
                  resetSignal={resetCounter}
                />
              )}
              {result && (
                <BreakEvenBar
                  key={`bee-${resetCounter}`}
                  inputs={inputs}
                  settings={settings}
                  currencySymbol={result.currencySymbol || '$'}
                  resetSignal={resetCounter}
                />
              )}

              {/* My Products — saved cost catalog. */}
              <MyProductsPanel
                products={scenarios}
                currentResult={result}
                currentInputs={inputs}
                currentSettings={settings}
                onSave={saveScenario}
                onLoad={loadScenario}
                onDelete={deleteScenario}
                onLoadExamples={loadExampleProducts}
              />

              {/* Growth Studio — Volume Sandbox + Growth Planner combined. */}
              {result && (
                <div className="sketch-card">
                  <h2 className="font-display text-2xl text-ink mb-1">🚀 Growth Studio</h2>
                  <p className="font-body text-ink/72 mb-4" style={{ fontSize: '0.92rem' }}>
                    Stress-test volumes and growth scenarios. Find the plan you want to commit to.
                  </p>
                  <div className="space-y-6">
                    <SensitivityTable
                      currencySymbol={result.currencySymbol}
                      inputs={inputs}
                      settings={settings}
                      setSettings={setSettings}
                    />
                    <ForecastPanel
                      inputs={inputs}
                      settings={settings}
                      currencySymbol={result.currencySymbol}
                    />
                  </div>
                </div>
              )}

              {/* Bottom tabs — Platforms / Updates / Cheat Sheet. */}
              <div>
                <TabStrip tabs={BOTTOM_TABS} active={bottomTab} onChange={setBottomTab} />
                {renderBottomTab()}
              </div>

              {/* Currency Converter at the very bottom. */}
              <CurrencyConverter />
            </section>
          </div>
        )}

        </div>
      </div>

      {/* SEO content sections — below the calculator */}
      <FAQSection />
      <ArticlesSection />
      <ContactSection />
      <Footer />
    </>
  );
}

// ─── App router ──────────────────────────────────────────────────────
//
// HomePage (above) is the calculator + SEO sections at /.
// ArticlePage is each individual /articles/:slug page — gets its own
// document.title + meta description for SEO.
export default function App() {
  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/articles/:slug" element={<ArticlePage />} />
      </Routes>
      <Analytics />
    </>
  );
}
